import { Link } from 'react-router-dom';
import { ShoppingBag, Search, Sun, Moon, Menu, X, UtensilsCrossed } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useStore } from '@/store';
import { useStoreInfo } from '@/lib/store';
import { motion, AnimatePresence } from 'framer-motion';

export function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const cart = useStore((state) => state.cart);
  const darkMode = useStore((state) => state.darkMode);
  const toggleDarkMode = useStore((state) => state.toggleDarkMode);
  const { storeInfo } = useStoreInfo();

  const navigation = [
    { name: 'Home', href: '/home' },
    { name: 'Products', href: '/products' },
    { name: 'Categories', href: '/categories' },
    { name: 'Track Order', href: '/track' },
    { name: 'About Us', href: '/about' },
    { name: 'Contact Us', href: '/contact' },
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setSearchOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      <header className="sticky top-0 z-50 bg-white dark:bg-gray-800 shadow-sm">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center">
              <Link to="/" className="flex items-center space-x-3">
                {storeInfo?.site_logo ? (
                  <img 
                    src={`https://marivieint.com/${storeInfo.site_logo}`}
                    alt={storeInfo.site_name} 
                    className="h-8 w-auto"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = 'https://via.placeholder.com/32';
                    }}
                  />
                ) : (
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex items-center space-x-2"
                  >
                    <b className="h-6 w-6 text-brand-primary" />
                    <span className="text-xl font-display font-bold bg-gradient-to-r from-brand-primary to-brand-light bg-clip-text text-transparent">
                      {storeInfo?.site_name || 'Store'}
                    </span>
                  </motion.div>
                )}
              </Link>
            </div>

            <nav className="hidden lg:flex lg:flex-1 lg:items-center lg:justify-center">
              <ul className="flex space-x-1">
                {navigation.map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className="relative px-4 py-2 rounded-full text-gray-700 hover:text-brand-primary dark:text-gray-300 dark:hover:text-brand-light transition-colors group"
                    >
                      <span className="relative z-10">{item.name}</span>
                      <motion.div
                        className="absolute inset-0 bg-gray-100 dark:bg-gray-700 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        layoutId="navBackground"
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          
            <div className="flex items-center gap-2">
              <motion.div
                ref={searchRef}
                className="relative hidden md:block"
                whileHover={{ scale: 1.02 }}
              >
                <button
                  onClick={() => setSearchOpen(!searchOpen)}
                  className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <Search className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                </button>

                <AnimatePresence>
                  {searchOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-4 border border-gray-200 dark:border-gray-700"
                    >
                      <input
                        type="text"
                        placeholder="Search products..."
                        className="w-full px-4 py-2 rounded-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

              <motion.button
                onClick={toggleDarkMode}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                {darkMode ? (
                  <Sun className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                ) : (
                  <Moon className="h-5 w-5 text-gray-500" />
                )}
              </motion.button>

              <Link to="/cart" className="relative p-2">
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <ShoppingBag className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                  {cart.length > 0 && (
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-brand-primary text-[10px] text-white"
                    >
                      {cart.length}
                    </motion.span>
                  )}
                </motion.div>
              </Link>

              <motion.button
                onClick={() => setIsOpen(!isOpen)}
                className="lg:hidden p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                {isOpen ? (
                  <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                ) : (
                  <Menu className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                )}
              </motion.button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, x: -300 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -300 }}
            className="fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 shadow-xl lg:hidden"
          >
            <div className="p-4">
              <div className="flex items-center justify-between mb-8">
                {storeInfo?.site_logo ? (
                  <img 
                    src={`https://marivieint.com/${storeInfo.site_logo}`}
                    alt={storeInfo.site_name}
                    className="h-8 w-auto"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = 'https://via.placeholder.com/32';
                    }}
                  />
                ) : (
                  <span className="text-xl font-display font-bold bg-gradient-to-r from-brand-primary to-brand-light bg-clip-text text-transparent">
                    {storeInfo?.site_name || 'Store'}
                  </span>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                </button>
              </div>
              <nav>
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="block px-4 py-3 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors mb-1"
                    onClick={() => setIsOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
              </nav>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}